import React, { useState } from 'react';
import { Field } from 'formik';
import FileDropzone from '../../common/FileDropzone';

const InformationsApicoles = ({ formikProps }) => {
  const { errors, touched, setFieldValue, values } = formikProps;
  const [uploading, setUploading] = useState({});
  const [fileErrors, setFileErrors] = useState({});

  const organisme = values.organisme;

  // === Section Documents ===
  const getAllRequiredDocuments = () => {
    const commonDocs = [];
    const organismeDocs = {
      'SAR': [
        { id: 'declaration_ruches', label: 'Déclaration de ruches (campagne de déclaration de septembre à décembre N-1)', required: true, category: 'SAR' }
      ],
      'AMAIR': [
        { id: 'declaration_ruches', label: 'Déclaration de ruches (campagne de déclaration de septembre à décembre N-1)', required: true, category: 'AMAIR' }
      ]
    };
    return [...commonDocs, ...(organismeDocs[organisme] || [])];
  };

  const getExistingFile = (category, docId) => {
    const fieldName = category === 'commun' ? 'documentsCommuns' : `documents${category}`;
    const documents = values[fieldName] || [];
    return documents.find(doc => doc.type === docId);
  };

  const handleFileRemove = (category, docId) => {
    const fieldName = category === 'commun' ? 'documentsCommuns' : `documents${category}`;
    const documents = values[fieldName] || [];
    const updatedDocuments = documents.filter(doc => doc.type !== docId);
    setFieldValue(fieldName, updatedDocuments);
  };

  const handleFileUpload = async (file, documentType, category) => {
    if (!file) return;

    try {
      setUploading(prev => ({ ...prev, [documentType]: true }));
      setFileErrors(prev => ({ ...prev, [documentType]: null }));

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Le fichier est trop volumineux (max 5MB)');
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Type de fichier non autorisé. Formats acceptés: JPEG, PNG, PDF');
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const fileData = {
          nom: file.name.replace(/\.[^/.]+$/, ''),
          nomOriginal: file.name,
          type: documentType,
          organisme: category,
          mimeType: file.type,
          taille: file.size,
          base64Data: e.target.result,
          isTemporary: true
        };

        const fieldName = category === 'commun' ? 'documentsCommuns' : `documents${category}`;
        const existingDocuments = values[fieldName] || [];
        const filteredDocuments = existingDocuments.filter(doc => doc.type !== documentType);
        setFieldValue(fieldName, [...filteredDocuments, fileData]);
      };

      reader.onerror = () => {
        throw new Error('Erreur lors de la lecture du fichier');
      };

      reader.readAsDataURL(file);
    } catch (error) {
      setFileErrors(prev => ({
        ...prev,
        [documentType]: error.message || 'Erreur lors du traitement du fichier'
      }));
    } finally {
      setUploading(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const documents = getAllRequiredDocuments();

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Informations apicoles
        </h3>
      </div>
      {/* Encart d'information */}
      <div className="bg-honey-50 border border-honey-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-honey-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-honey-700">
              <span className="font-medium">Important :</span> Les informations ci-dessous doivent correspondre à celles figurant sur votre déclaration de ruches que vous allez télécharger en bas de page.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">

        <div>
          <label htmlFor="nombreRuchers" className="block text-sm font-medium text-gray-700">
            Nombre d'emplacements <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <Field
              type="number"
              name="informationsApicoles.nombreRuchers"
              id="nombreRuchers"
              min="1"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsApicoles?.nombreRuchers && touched.informationsApicoles?.nombreRuchers && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsApicoles.nombreRuchers}</p>
            )}
          </div>
        </div>
        
        <div>
          <label htmlFor="nombreRuches" className="block text-sm font-medium text-gray-700">
            Nombre de ruches <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <Field
              type="number"
              name="informationsApicoles.nombreRuches"
              id="nombreRuches"
              min="1"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsApicoles?.nombreRuches && touched.informationsApicoles?.nombreRuches && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsApicoles.nombreRuches}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="departement" className="block text-sm font-medium text-gray-700">
            Département <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsApicoles.localisation.departement"
              id="departement"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsApicoles?.localisation?.departement && touched.informationsApicoles?.localisation?.departement && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsApicoles.localisation.departement}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="commune" className="block text-sm font-medium text-gray-700">
            Commune <span className="text-red-500">*</span>
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsApicoles.localisation.commune"
              id="commune"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsApicoles?.localisation?.commune && touched.informationsApicoles?.localisation?.commune && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsApicoles.localisation.commune}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="napi" className="block text-sm font-medium text-gray-700">
            Numéro NAPI (optionnel)
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsApicoles.napi"
              id="napi"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsApicoles?.napi && touched.informationsApicoles?.napi && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsApicoles.napi}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="numeroAmexa" className="block text-sm font-medium text-gray-700">
            Numéro AMEXA (optionnel)
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsApicoles.numeroAmexa"
              id="numeroAmexa"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsApicoles?.numeroAmexa && touched.informationsApicoles?.numeroAmexa && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsApicoles.numeroAmexa}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="siret" className="block text-sm font-medium text-gray-700">
            SIRET (optionnel)
          </label>
          <div className="mt-1">
            <Field
              type="text"
              name="informationsApicoles.siret"
              id="siret"
              placeholder="14 chiffres exactement (ex: 12345678901234)"
              maxLength="14"
              onInput={(e) => {
                // Ne garder que les chiffres
                e.target.value = e.target.value.replace(/\D/g, '');
              }}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
            />
            {errors.informationsApicoles?.siret && touched.informationsApicoles?.siret && (
              <p className="mt-2 text-sm text-red-600">{errors.informationsApicoles.siret}</p>
            )}
          </div>
        </div>
      </div>

      {/* Section Documents requis */}
      <div className="border-t border-gray-200 pt-8">
        <h4 className="text-md font-medium text-gray-900 mb-4">
          Documents requis
        </h4>
        <p className="text-sm text-gray-500 mb-6">
          Veuillez fournir les documents suivants pour compléter votre adhésion
        </p>

        <div className="grid grid-cols-1 gap-6">
          {documents.map((doc) => {
            const existingFile = getExistingFile(doc.category, doc.id);
            return (
              <div key={doc.id} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {doc.label}{doc.required && <span className="text-red-500"> *</span>}
                </label>
                
                {existingFile ? (
                  <div className="mt-1 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <svg className="w-8 h-8 mr-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-900">{existingFile.nomOriginal || existingFile.nom}</p>
                          <p className="text-xs text-green-700 mt-0.5">
                            {(existingFile.taille / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(doc.category, doc.id)}
                        className="ml-3 p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full transition-colors"
                        title="Supprimer le fichier"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <FileDropzone
                    onFileSelect={(file) => handleFileUpload(file, doc.id, doc.category)}
                    uploading={uploading[doc.id]}
                    error={fileErrors[doc.id]}
                    label="Télécharger un fichier"
                    required={doc.required}
                    maxSize={5 * 1024 * 1024}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InformationsApicoles;
